import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Footer from '../components/shared/Footer';
import { 
  ArrowRight,
  Zap,
  Shield,
  BarChart3,
  Star,
  ChevronRight,
  Play,
  TrendingUp,
  TrendingDown,
  Smartphone,
  Twitter,
  Instagram
} from 'lucide-react';
import { statsAPI, testimonialsAPI, signalsAPI, publicAPI } from '../services/api';
import io from 'socket.io-client';


// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Components
const Badge = ({ children }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'var(--primary-light)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: 'var(--radius-pill)',
    color: 'var(--primary-accent)',
    fontSize: 13,
    fontWeight: 500
  }}>
    {children}
  </span>
);

const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.5, delay }}
      style={{
        padding: 32,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        transition: 'var(--transition)'
      }}
      whileHover={{ borderColor: 'var(--border-bright)', y: -2 }}
    >
      <div style={{
        width: 48,
        height: 48,
        background: 'var(--primary-light)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20
      }}>
        <Icon size={22} color="var(--primary-accent)" />
      </div>
      <h3 style={{ 
        fontSize: 18, 
        fontWeight: 600, 
        marginBottom: 10,
        color: 'var(--text-primary)'
      }}>{title}</h3>
      <p style={{ 
        fontSize: 14, 
        color: 'var(--text-secondary)', 
        lineHeight: 1.6 
      }}>{description}</p>
    </motion.div>
  );
};

const StepCard = ({ number, title, description, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.5, delay }}
      style={{ 
        display: 'flex', 
        gap: 20,
        padding: 24,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)'
      }}
    >
      <div style={{
        width: 40,
        height: 40,
        background: 'var(--gradient-primary)',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 16,
        fontWeight: 700,
        color: 'white'
      }}>
        {number}
      </div>
      <div>
        <h4 style={{ 
          fontSize: 16, 
          fontWeight: 600, 
          marginBottom: 6,
          color: 'var(--text-primary)'
        }}>{title}</h4>
        <p style={{ 
          fontSize: 14, 
          color: 'var(--text-secondary)', 
          lineHeight: 1.6
        }}>{description}</p>
      </div>
    </motion.div>
  );
};

const TestimonialCard = ({ quote, rating = 5, display_name, display_role, photo_url, delay = 0 }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.5, delay }}
      style={{
        padding: 28,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}
    >
      <div style={{ display: 'flex', gap: 4 }}>
        {[1,2,3,4,5].map(i => (
          <Star key={i} size={14} fill={i <= rating ? "var(--primary-accent)" : "none"} color={i <= rating ? "var(--primary-accent)" : "var(--text-muted)"} />
        ))}
      </div>
      <p style={{ 
        fontSize: 15, 
        color: 'var(--text-primary)', 
        lineHeight: 1.7,
        flex: 1
      }}>"{quote}"</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {photo_url && (
          <img 
            src={photo_url} 
            alt={display_name} 
            style={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              objectFit: 'cover' 
            }} 
          />
        )}
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{display_name}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{display_role}</div>
        </div>
      </div>
    </motion.div>
  );
};

const StatItem = ({ value, label, trend }) => (
  <div style={{ textAlign: 'center', minWidth: 160 }}>
    <div style={{ 
      position: 'relative',
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <div style={{ 
        fontSize: 'clamp(28px, 4vw, 36px)', 
        fontWeight: 800, 
        color: 'var(--text-primary)',
        lineHeight: 1,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: 'var(--font-mono)'
      }}>
        {value}
        {trend && (
          <span style={{ 
            fontSize: 12, 
            color: trend.startsWith('+') ? 'var(--success)' : 'var(--error)',
            fontWeight: 700,
            background: trend.startsWith('+') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-pill)',
            whiteSpace: 'nowrap'
          }}>
            {trend}
          </span>
        )}
      </div>
      <div style={{ 
        fontSize: 12, 
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: 700,
        whiteSpace: 'nowrap'
      }}>{label}</div>
    </div>
  </div>
);

const ResultCard = ({ result }) => {
  const isWin = result.result === 'win';
  const color = isWin ? 'var(--success)' : 'var(--error)';
  const isBot = result.is_automated;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        padding: '10px 16px',
        background: 'var(--bg-navbar)',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${color}`,
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        minWidth: 320
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{
          padding: '2px 6px',
          background: isBot ? 'rgba(99, 102, 241, 0.1)' : 'rgba(168, 85, 247, 0.1)',
          borderRadius: 4,
          fontSize: 8,
          fontWeight: 900,
          color: isBot ? 'var(--primary-accent)' : '#a855f7',
          letterSpacing: '0.05em',
          width: 'fit-content'
        }}>
          {isBot ? 'AI ALPHA' : 'TEAM SETUP'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            padding: '4px 8px',
            background: isWin ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 800,
            color: color,
            letterSpacing: '0.05em'
          }}>
            {isWin ? 'WIN' : 'LOSS'}
          </div>
          <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
            {result.pair}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        {isWin ? <TrendingUp size={14} color={color} /> : <TrendingDown size={14} color={color} />}
        <div className="mono" style={{ 
          fontSize: 15, 
          fontWeight: 700, 
          color: color 
        }}>
          {result.profit_percentage > 0 ? '+' : ''}{result.profit_percentage}%
        </div>
      </div>

      <div style={{ 
        fontSize: 10, 
        color: 'var(--text-muted)', 
        textTransform: 'uppercase',
        borderLeft: '1px solid var(--border)',
        paddingLeft: 12,
        marginLeft: 4,
        fontFamily: 'var(--font-mono)'
      }}>
        {new Date(result.closed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </motion.div>
  );
};

// Main Page
export default function LandingPage() {
  const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const [userCount, setUserCount] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [stats, setStats] = useState({ 
    win_rate: 0, 
    total_profit: 0, 
    win_rate_trend: null, 
    profit_trend: null 
  });

  // CMS Content State — cmsLoaded stays false until the API call resolves
  // so the hardcoded defaults are NEVER shown to the user (eliminates flicker)
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [cmsContent, setCmsContent] = useState({
    badge: '🚀 6-12 Month Live Beta: Proving Accuracy',
    hero_title: 'Real-Time AI Trading Intelligence',
    hero_subtitle: 'SignalBros is currently in a public Live Beta phase to verify our Alpha algorithm\'s accuracy across BTC, Gold, and Forex. Join the test today and get a FREE upgrade to the PRO Tier.',
    features_title: 'The Alpha Edge',
    features_subtitle: 'Institutional-grade intelligence, refined through 365 days of live market validation.',
    feature_1_title: 'Proprietary Alpha Engine',
    feature_1_desc: 'Advanced AI logic that detects high-probability SMC setups including Market Structure Shifts and Liquidity Sweeps in real-time.',
    feature_2_title: '12-Month Accuracy Test',
    feature_2_desc: 'Total transparency. We are documenting a year of live performance to verify our algorithm\'s edge against global market volatility.',
    feature_3_title: 'Precision Scalping',
    feature_3_desc: 'Optimized execution for high-liquidity assets. Capture consistent moves in BTC, Gold, and Forex with surgical entry and exit timing.',
    feature_4_title: 'Dynamic Risk Control',
    feature_4_desc: 'Protect your capital with built-in risk protocols. Each signal features automated SL/TP levels designed for maximum trade longevity.'
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [cmsRes, resultsRes, statsRes, userCountRes, testimonialsRes] = await Promise.all([
          publicAPI.getLandingContent().catch(() => ({ data: { success: false } })),
          signalsAPI.getPublicRecentResults().catch(() => ({ data: { success: false } })),
          signalsAPI.getStats().catch(() => ({ data: { success: false } })),
          statsAPI.getUserCount().catch(() => ({ data: { success: false } })),
          testimonialsAPI.getApproved().catch(() => ({ data: { success: false } }))
        ]);

        if (cmsRes.data.success && cmsRes.data.data) {
          setCmsContent(prev => ({ ...prev, ...cmsRes.data.data }));
        }
        // Mark CMS as loaded regardless — if DB has no overrides,
        // we show the hardcoded defaults only after this point (no flicker)
        setCmsLoaded(true);

        if (resultsRes.data.success && Array.isArray(resultsRes.data.data)) {
          setRecentResults(resultsRes.data.data);
        }

        if (statsRes.data.success && statsRes.data.data) {
          const data = statsRes.data.data;
          setStats({
            win_rate: parseFloat(data.win_rate || 0),
            total_profit: parseFloat(data.total_profit || 0),
            win_rate_trend: data.win_rate_trend,
            profit_trend: data.profit_trend
          });
        }

        if (userCountRes.data.success && userCountRes.data.data) {
          setUserCount(userCountRes.data.data.count || 0);
        }

        if (testimonialsRes.data.success && Array.isArray(testimonialsRes.data.testimonials)) {
          setTestimonials(testimonialsRes.data.testimonials);
        } else {
          setTestimonials([]);
        }
      } catch (err) {
        console.error('Failed to fetch initial landing data:', err);
      }
    };

    fetchInitialData();

    // Determine the base socket URL from REACT_APP_API_URL so it doesn't fall back to localhost in production
    const socketUrl = process.env.REACT_APP_API_URL 
      ? process.env.REACT_APP_API_URL.replace('/api', '') 
      : 'http://localhost:5000';
    const socket = io(socketUrl);
    socket.on('signal:closed_public', (newResult) => {
      if (newResult) {
        setRecentResults(prev => Array.isArray(prev) ? [newResult, ...prev].slice(0, 10) : [newResult]);
        // Refresh stats on closure
        signalsAPI.getStats().then(res => {
          if (res.data?.success && res.data.data) {
            const data = res.data.data;
            setStats({
              win_rate: parseFloat(data.win_rate || 0),
              total_profit: parseFloat(data.total_profit || 0),
              win_rate_trend: data.win_rate_trend,
              profit_trend: data.profit_trend
            });
          }
        });
      }
    });

    // Refresh user count periodically
    const interval = setInterval(async () => {
      try {
        const res = await statsAPI.getUserCount();
        if (res.data?.success && res.data.data) {
          setUserCount(res.data.data.count || 0);
        }
      } catch (err) {}
    }, 30000);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, []);

  const formatUserCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K+`;
    }
    return count.toString();
  };


  const steps = [
    { title: 'Select Your Strategy', description: 'Choose the subscription tier that aligns with your goals, from essential signals to full institutional breakdowns.' },
    { title: 'Monitor the Bias', description: 'Access our live institutional bias dashboard to stay on the right side of the trend across all major timeframes.' },
    { title: 'Execute with Confidence', description: 'Receive precise entry and tiered take-profit levels delivered instantly to your preferred platform.' }
  ];

  return (
    <div style={{ background: 'var(--bg-primary)' }}>
      
      {/* Hero Section */}
      <section style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center',
        paddingTop: 120,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background gradient */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          height: '100%',
          background: 'var(--gradient-hero)',
          pointerEvents: 'none'
        }} />


        
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 32,
            paddingBottom: 64,
            maxWidth: 800,
            margin: '0 auto'
          }}>
            <motion.div
              ref={heroRef}
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              variants={stagger}
            >
              <motion.div variants={fadeInUp} transition={{ duration: 0.5 }} style={{ display: 'flex', justifyContent: 'center', minHeight: 36, marginTop: 24 }}>
                {cmsLoaded && (
                  <Badge>
                    <Zap size={14} />
                    {cmsContent.badge}
                  </Badge>
                )}
              </motion.div>
              
              <motion.h1
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{
                  fontSize: 'clamp(32px, 5vw, 64px)',
                  fontWeight: 800,
                  marginTop: 24,
                  marginBottom: 20,
                  lineHeight: 1.1,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.03em',
                  opacity: cmsLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease'
                }}
              >
                {cmsContent.hero_title}
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{
                  fontSize: 'clamp(16px, 2vw, 20px)',
                  color: 'var(--text-secondary)',
                  marginBottom: 40,
                  lineHeight: 1.6,
                  opacity: cmsLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease 0.1s'
                }}
              >
                {cmsContent.hero_subtitle}
              </motion.p>
              
              <motion.div 
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: 0.3 }}
                style={{ 
                  display: 'flex', 
                  gap: 16,
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <Link to="/register" style={{ 
                  background: 'var(--primary-accent)', 
                  color: 'white', 
                  padding: '16px 32px', 
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  textDecoration: 'none',
                  transition: 'var(--transition)',
                  boxShadow: '0 10px 30px -10px var(--primary-accent)'
                }}>
                  Get started free <ArrowRight size={20} />
                </Link>
                <a href="/signalbros.apk" download="SignalBros.apk" style={{ 
                  background: 'var(--bg-secondary)', 
                  border: '1px solid var(--border-bright)',
                  color: 'var(--text-primary)', 
                  padding: '16px 32px', 
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 16,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  textDecoration: 'none',
                  transition: 'var(--transition)'
                }}>
                  <Smartphone size={20} /> Download App
                </a>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                transition={{ duration: 0.5, delay: 0.4 }}
                style={{
                  display: 'flex',
                  gap: 16,
                  justifyContent: 'center',
                  marginTop: 24
                }}
              >
                <a
                  href={cmsContent?.twitter_link || 'https://x.com/Signal_Bros'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    transition: 'var(--transition)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = 'var(--text-primary)';
                    e.currentTarget.style.borderColor = 'var(--border-bright)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  @Signal_Bros
                </a>
                <a
                  href={cmsContent?.instagram_link || 'https://www.instagram.com/signalbrosteams/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: 'var(--text-secondary)',
                    textDecoration: 'none',
                    transition: 'var(--transition)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = 'var(--text-primary)';
                    e.currentTarget.style.borderColor = 'var(--border-bright)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Instagram size={18} />
                  @signalbrosteams
                </a>
              </motion.div>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div 
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ 
              display: 'flex', 
              justifyContent: 'center',
              gap: 'clamp(24px, 5vw, 48px)',
              paddingTop: 48,
              borderTop: '1px solid var(--border)',
              flexWrap: 'wrap'
            }}
          >
            <StatItem value={formatUserCount(userCount)} label="Traders Joined" />
            <StatItem 
              value={`${stats.win_rate}%`} 
              label="Alpha Winrate" 
              trend={stats.win_rate_trend} 
            />
            <StatItem 
              value={`${stats.total_profit > 0 ? '+' : ''}${stats.total_profit.toFixed(1)}%`} 
              label="Alpha Profits" 
              trend={stats.profit_trend} 
            />
            <StatItem value="24/7" label="System Uptime" />
          </motion.div>
        </div>
      </section>

      {/* Recent Results Scroller */}
      {Array.isArray(recentResults) && recentResults.length > 0 && (
        <section style={{ padding: '24px 0', background: 'var(--bg-elevated)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ 
              flexShrink: 0, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12, 
              paddingRight: 24, 
              borderRight: '1px solid var(--border)',
              zIndex: 2,
              background: 'var(--bg-card)'
            }}>
              <div style={{ position: 'relative', width: 10, height: 10 }}>
                <div style={{ 
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'var(--success)',
                  opacity: 0.4,
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }} />
                <div style={{ 
                  position: 'relative',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: 'var(--success)',
                  boxShadow: '0 0 10px var(--success)'
                }} />
              </div>
              <span style={{ 
                fontSize: 12, 
                fontWeight: 800, 
                color: 'var(--text-primary)', 
                whiteSpace: 'nowrap', 
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>Live Feed</span>
            </div>
            
            <div className="marquee-container">
              <div className="marquee-content">
                {/* Original items */}
                {recentResults.map((res, i) => (
                  <ResultCard key={`orig-${i}`} result={res} />
                ))}
                {/* Duplicated items for seamless loop */}
                {recentResults.map((res, i) => (
                  <ResultCard key={`dup-${i}`} result={res} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Supported Assets Section */}
      <section style={{ padding: '80px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <motion.div variants={fadeInUp}>
              <Badge>Supported Assets</Badge>
            </motion.div>
            <motion.h2
              variants={fadeInUp}
              style={{
                fontSize: 32,
                fontWeight: 700,
                marginTop: 16,
                marginBottom: 12,
                color: 'var(--text-primary)'
              }}
            >
              Trade signals for top assets
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              style={{
                fontSize: 16,
                color: 'var(--text-secondary)',
                maxWidth: 480,
                margin: '0 auto'
              }}
            >
              Get accurate trading signals for cryptocurrencies and forex pairs.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 16
            }}
          >
            {[
              { symbol: 'BTC', name: 'Bitcoin', icon: '/BTC.png', active: true },
              { symbol: 'ETH', name: 'Ethereum', icon: '/ETH.png', active: true },
              { symbol: 'BNB', name: 'Binance Coin', icon: '/BNB.png', active: true },
              { symbol: 'SOL', name: 'Solana', icon: '/SOL.jpeg', active: true },
              { symbol: 'XRP', name: 'Ripple', icon: '/XRP.png', active: true },
              { symbol: 'XAUUSD', name: 'Gold', icon: '/XAU.png', active: true },
              { symbol: 'EUR/USD', name: 'Euro/USD', icon: '/EUR_USD.png', active: true },
              { symbol: 'AUD/USD', name: 'Aussie/USD', icon: '/AUD.png', active: true },
              { symbol: 'GBP/USD', name: 'GBP/USD', icon: '/GPB.png', active: true },
            ].map((asset, i) => (
              <motion.div
                key={asset.symbol}
                variants={fadeInUp}
                whileHover={{ 
                  scale: 1.05, 
                  borderColor: 'rgba(99, 102, 241, 0.4)',
                  boxShadow: '0 10px 30px -10px rgba(99, 102, 241, 0.2)',
                  y: -5
                }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 400, 
                  damping: 17,
                  delay: i * 0.05 
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '16px 24px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  minWidth: 180,
                  cursor: 'default'
                }}
              >
                <img
                  src={asset.icon}
                  alt={asset.name}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    opacity: asset.active ? 1 : 0.5,
                    filter: asset.active ? 'none' : 'grayscale(50%)'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    {asset.symbol}
                    {!asset.active && (
                      <span style={{
                        fontSize: 10,
                        padding: '2px 6px',
                        background: 'rgba(251, 191, 36, 0.1)',
                        color: '#fbbf24',
                        borderRadius: 4,
                        fontWeight: 500
                      }}>
                        SOON
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: 12,
                    color: 'var(--text-muted)'
                  }}>
                    {asset.name}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{ padding: 'clamp(64px, 10vw, 120px) 0' }}>
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <motion.div variants={fadeInUp}>
              <Badge>Features</Badge>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              style={{ 
                fontSize: 36, 
                fontWeight: 700,
                marginTop: 16,
                marginBottom: 12,
                color: 'var(--text-primary)'
              }}
            >
              {cmsContent.features_title}
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              style={{ 
                fontSize: 16, 
                color: 'var(--text-secondary)',
                maxWidth: 480,
                margin: '0 auto'
              }}
            >
              {cmsContent.features_subtitle}
            </motion.p>
          </motion.div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: 24
          }}>
            {[
              { icon: Zap, title: cmsContent.feature_1_title, description: cmsContent.feature_1_desc },
              { icon: BarChart3, title: cmsContent.feature_2_title, description: cmsContent.feature_2_desc },
              { icon: Shield, title: cmsContent.feature_3_title, description: cmsContent.feature_3_desc },
              { icon: Star, title: cmsContent.feature_4_title, description: cmsContent.feature_4_desc }
            ].map((feature, i) => (
              <FeatureCard key={i} {...feature} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Discord Community Section */}
      <section id="discord" style={{ padding: 'clamp(48px, 8vw, 80px) 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            style={{ textAlign: 'center', marginBottom: 48 }}
          >
            <motion.div variants={fadeInUp}>
              <Badge>
                <div style={{ 
                  width: 20, 
                  height: 20, 
                  background: 'rgba(99, 102, 241, 0.2)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  marginRight: 2 
                }}>
                  <img 
                    src="/Discord.png" 
                    alt="Discord" 
                    style={{ width: 12, height: 12, borderRadius: '50%', objectFit: 'cover' }} 
                  />
                </div>
                Discord Community
              </Badge>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              style={{ 
                fontSize: 32, 
                fontWeight: 700,
                marginTop: 16,
                marginBottom: 12,
                color: 'var(--text-primary)'
              }}
            >
              Join Our Trading Communities
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              style={{ 
                fontSize: 16, 
                color: 'var(--text-secondary)',
                maxWidth: 520,
                margin: '0 auto'
              }}
            >
              Connect with fellow traders, get real-time alerts, and access exclusive insights on Discord.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 20,
              maxWidth: 1000,
              margin: '0 auto'
            }}
          >
            {[
              { name: 'Free', label: 'Preview Community', color: 'var(--text-muted)' },
              { name: 'Basic', label: 'Join Basic Community', color: 'var(--primary-accent)' },
              { name: 'Pro', label: 'Join Pro Community', color: '#8b5cf6', popular: true },
              { name: 'VIP', label: 'Join VIP Community', color: '#f59e0b' }
            ].map((tier, i) => (
              <motion.div
                key={tier.name}
                variants={fadeInUp}
                whileHover={{ 
                  y: -10, 
                  borderColor: tier.color === 'var(--text-muted)' ? 'rgba(113, 113, 122, 0.4)' : tier.color + '4d',
                  boxShadow: `0 20px 40px -20px ${tier.color === 'var(--text-muted)' ? 'rgba(113, 113, 122, 0.2)' : tier.color + '33'}`
                }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 300, 
                  damping: 20,
                  delay: i * 0.1 
                }}
                style={{
                  padding: '24px',
                  background: 'var(--bg-card)',
                  border: tier.popular ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  textAlign: 'center',
                  position: 'relative',
                  cursor: 'default'
                }}
              >
                {tier.popular && (
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#8b5cf6',
                    color: 'white',
                    padding: '3px 10px',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 10,
                    fontWeight: 600
                  }}>
                    Popular
                  </div>
                )}
                <div style={{
                  width: 48,
                  height: 48,
                  background: `${tier.color}15`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: 24
                }}>
                  <img src="/Discord.png" alt="Discord" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                </div>
                <h4 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: tier.color
                }}>
                  {tier.name}
                </h4>
                <p style={{
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  marginBottom: 16
                }}>
                  {tier.name === 'Free' ? 'Community chat & basic updates' : 
                   tier.name === 'Basic' ? 'Signal alerts & market analysis' :
                   tier.name === 'Pro' ? 'Priority alerts & exclusive insights' :
                   'VIP Auto-trading & direct support'}
                </p>
                <Link 
                  to="/pricing"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '10px 20px',
                    background: tier.popular ? '#8b5cf6' : 'var(--bg-elevated)',
                    color: tier.popular ? 'white' : 'var(--text-primary)',
                    border: tier.popular ? 'none' : '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: 'none',
                    transition: 'var(--transition)'
                  }}
                >
                  {tier.label} <ChevronRight size={14} />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" style={{ padding: '120px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 64,
            alignItems: 'center'
          }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div variants={fadeInUp}>
                <Badge>How it works</Badge>
              </motion.div>
              <motion.h2 
                variants={fadeInUp}
                style={{ 
                  fontSize: 36, 
                  fontWeight: 700,
                  marginTop: 16,
                  marginBottom: 12,
                  color: 'var(--text-primary)'
                }}
              >
                Get started in minutes
              </motion.h2>
              <motion.p 
                variants={fadeInUp}
                style={{ 
                  fontSize: 16, 
                  color: 'var(--text-secondary)',
                  marginBottom: 32,
                  lineHeight: 1.6
                }}
              >
                Our streamlined onboarding process gets you up and running quickly so you can focus on what matters most.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Link to="/register" style={{ 
                  background: 'var(--primary-accent)', 
                  color: 'white', 
                  padding: '12px 24px', 
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 500,
                  fontSize: 15,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  textDecoration: 'none'
                }}>
                  Start free trial <ArrowRight size={18} />
                </Link>
              </motion.div>
            </motion.div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {steps.map((step, i) => (
                <StepCard key={i} number={i + 1} {...step} delay={i * 0.1} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{ padding: '120px 0' }}>
        <div className="container">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            style={{ textAlign: 'center', marginBottom: 64 }}
          >
            <motion.div variants={fadeInUp}>
              <Badge><Star size={14} /> Testimonials</Badge>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              style={{ 
                fontSize: 36, 
                fontWeight: 700,
                marginTop: 16,
                marginBottom: 12,
                color: 'var(--text-primary)'
              }}
            >
              Loved by teams worldwide
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              style={{ 
                fontSize: 16, 
                color: 'var(--text-secondary)',
                maxWidth: 480,
                margin: '0 auto'
              }}
            >
              See what our customers have to say about their experience.
            </motion.p>
          </motion.div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 24
          }}>
            {Array.isArray(testimonials) && testimonials.length > 0 ? (
              testimonials.map((testimonial, i) => (
                <TestimonialCard 
                  key={testimonial.id || i} 
                  quote={testimonial.quote}
                  rating={testimonial.rating}
                  display_name={testimonial.display_name}
                  display_role={testimonial.display_role}
                  photo_url={testimonial.photo_url}
                  delay={i * 0.1} 
                />
              ))
            ) : (
              <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                No verified testimonials are available right now.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '120px 0' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-xl)',
              padding: 'clamp(32px, 8vw, 64px) clamp(24px, 6vw, 48px)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '150%',
              height: '150%',
              background: 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.1) 0%, transparent 50%)',
              pointerEvents: 'none'
            }} />
            
            <h2 style={{ 
              fontSize: 32, 
              fontWeight: 700, 
              marginBottom: 16, 
              color: 'var(--text-primary)',
              position: 'relative',
              zIndex: 1
            }}>
              Elevate to the Institutional Level
            </h2>
            <p style={{ 
              fontSize: 16, 
              color: 'var(--text-secondary)', 
              marginBottom: 32, 
              maxWidth: 480, 
              margin: '0 auto 32px',
              position: 'relative',
              zIndex: 1
            }}>
              Stop following the retail crowd. Start trading with the banks. Join thousands of elite members who rely on SignalBros for data-driven results.
            </p>            <div style={{ 
              display: 'flex', 
              gap: 12, 
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <Link to="/register" style={{
                background: 'var(--primary-accent)',
                color: 'white',
                padding: '14px 28px',
                borderRadius: 'var(--radius-md)',
                fontWeight: 500,
                fontSize: 15,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                textDecoration: 'none'
              }}>
                Get instant access <ChevronRight size={18} />
              </Link>
              <Link to="/pricing" style={{
                background: 'transparent',
                border: '1px solid var(--border-bright)',
                color: 'var(--text-primary)',
                padding: '14px 28px',
                borderRadius: 'var(--radius-md)',
                fontWeight: 500,
                fontSize: 15,
                textDecoration: 'none'
              }}>
                View pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer cmsContent={cmsContent} />
    </div>
  );
}
